import {
  ActivityLike,
  ActivityReply,
  Reaction,
} from "../../../../@types/activities.ts";
import {
  ActivityReactionBadges,
  ActivityReactionButton,
} from "../object/ActivityReactions.tsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faThumbsUp, faSpinner } from "@fortawesome/pro-regular-svg-icons";
import { faThumbsUp as faThumbsUpSolid } from "@fortawesome/pro-solid-svg-icons";
import { useMutation } from "react-query";
import { fetchApi } from "../../../../helpers/reactQueryApi.ts";
import { filter, isEmpty, omit, pick } from "lodash";
import LikesModal from "../object/LikesModal.tsx";
import { useState } from "react";
import { Membership } from "../../../../@types";
import { useUpdateActivityReplyInCache } from "../../../../hooks/useUpdateActivityReplyInCache.ts";
import TranslationControls from "@/components/shared/TranslationControls.tsx";
import ReplyContextMenu from "@/components/activitystreams/activity/replies/ReplyContextMenu.tsx";
import { useLongPress } from "@/hooks/useLongPress.ts";
import classNames from "classnames";

type ReplyInteractionsArgs = {
  replyId: string;
  activityId: string;
  likeCount: number;
  likes: ActivityLike[];
  myLike: ActivityLike | null;
  canLike: boolean;
  reactions?: Reaction[] | null;
  myReaction?: string | null;
  canReact?: boolean;
  type: "replies" | "answers";
  translatedContent: {
    language?: string;
    content?: string;
  };
  setTranslatedContent: (props: {
    language?: string;
    content?: string;
    quoted_content?: string;
  }) => void;
  can: ActivityReply["can"];
  quoteReply: () => void;
  setShowComposer: (show: boolean) => void;
};

export default function ReplyInteractions({
  activityId,
  replyId,
  likeCount,
  likes,
  myLike,
  canLike,
  reactions,
  myReaction,
  canReact,
  type,
  translatedContent,
  setTranslatedContent,
  can,
  quoteReply,
  setShowComposer,
}: ReplyInteractionsArgs) {
  const { updateReply } = useUpdateActivityReplyInCache();
  const [showLikes, setShowLikes] = useState(false);

  const { mutate: toggleLike, isLoading } = useMutation<
    Membership & {
      object_meta: { like_list_short: string; like_list_long: string };
    },
    Error
  >(
    () =>
      fetchApi(
        `/activities/${activityId}/${type}/${replyId}/${
          myLike ? "unlike" : "like"
        }.json`,
        {
          method: "POST",
        },
      ),
    {
      onSuccess: (data) => {
        updateReply(
          activityId,
          replyId,
          type,
          (reply) =>
            ({
              ...reply,
              my_like: myLike ? null : omit(data, "object_meta"),
              like_count: myLike ? reply.like_count - 1 : reply.like_count + 1,
              likes: myLike
                ? filter(reply.likes, (like) => like.id !== data.id)
                : [...reply.likes, omit(data, "object_meta")],
              ...pick(data.object_meta, ["like_list_long", "like_list_short"]),
            }) as ActivityReply,
        );
      },
    },
  );

  const reactionsOn =
    Preload.current_network.config?.activities?.reactions_enabled === true;

  const { mutate: toggleReaction } = useMutation(
    (emoji: string) =>
      fetchApi(
        `/activities/${activityId}/${type}/${replyId}/${
          myReaction === emoji ? "unreact" : "react"
        }.json`,
        myReaction === emoji
          ? { method: "POST" }
          : { method: "POST", body: { emoji } },
      ),
    {
      onSuccess: (data) => {
        if (!data) return;
        updateReply(
          activityId,
          replyId,
          type,
          (reply) =>
            ({
              ...reply,
              ...pick(data, ["reactions", "my_reaction"]),
            }) as ActivityReply,
        );
      },
    },
  );

  const handlers = useLongPress(
    () => setShowLikes(true),
    () => toggleLike(),
  );
  const buttonEvents = window.isApp ? handlers : {};

  return (
    <div className="reply-interactions mt-1 flex flex-col gap-1">
      {showLikes && likes && (
        <LikesModal likes={likes} close={() => setShowLikes(false)} />
      )}
      {reactionsOn && !isEmpty(reactions) && (
        <div className="reply-reactions flex flex-wrap items-center gap-1 py-1">
          <ActivityReactionBadges
            reactions={reactions}
            onSelect={toggleReaction}
          />
        </div>
      )}
      <div
        className={classNames("flex items-center", { "gap-2": window.isApp })}
      >
        {reactionsOn && canReact && (
          <ActivityReactionButton
            myReaction={myReaction}
            onSelect={toggleReaction}
          />
        )}
        {!reactionsOn && canLike && (
          <>
            {!window.isApp && (
              <button
                className="btn btn-sm text-xs hover:underline align-middle"
                role="button"
                onClick={(e) => {
                  e.preventDefault();
                  toggleLike();
                }}
              >
                {I18n.t("activitystreams.interesting")}
              </button>
            )}
            <button
              className="btn btn-sm text-sm hover:underline align-middle"
              role="button"
              onClick={
                !window.isApp
                  ? (e) => {
                      e.preventDefault();
                      setShowLikes(true);
                    }
                  : undefined
              }
              {...buttonEvents}
            >
              <FontAwesomeIcon
                className="text-primary mr-1"
                icon={
                  isLoading ? faSpinner : myLike ? faThumbsUpSolid : faThumbsUp
                }
                size={window.isApp ? "xl" : "lg"}
                spin={isLoading}
              />
              {likeCount}
            </button>
          </>
        )}
        {Preload.current_network.config?.translations?.deep_l?.enabled &&
          window.isApp && (
            <TranslationControls
              object_language={translatedContent.language || "ORIGINAL"}
              types={[
                type === "replies" ? "activity_comment" : "activity_answer",
              ]}
              target_id={`${activityId}:${replyId}`}
              resetTranslation={() =>
                setTranslatedContent({ language: undefined })
              }
              translateObject={(data, language) =>
                setTranslatedContent({
                  language,
                  content: data.text,
                  quoted_content: data.quoted_message_text,
                })
              }
            />
          )}
        {window.isApp && (
          <ReplyContextMenu
            can={can}
            replyId={replyId}
            activityId={activityId}
            setShowComposer={setShowComposer}
            quoteReply={quoteReply}
            type={type}
          />
        )}
      </div>
    </div>
  );
}
